import { useEffect, useRef, useState } from 'react'
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedClient } from '@/api/queries'
import { POLL_NORMAL } from '@/api/polling'
import { useSocketContext } from '@/contexts/socket-context'
import { useAuth } from '@/hooks/use-auth'
import type { ReplyAttachmentDraft } from '@/features/communications/types'
import type { ChatDetail, ChatList, ChatMessage, ChatMode, ChatView } from '@/features/storefront-chat/types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'

export const storefrontChatKeys = {
  all: ['storefront-chats'] as const,
  list: (view: ChatView, search: string) => ['storefront-chats', 'list', view, search] as const,
  summary: ['storefront-chats', 'summary'] as const,
  detail: (conversationId: string) => ['storefront-chats', 'detail', conversationId] as const,
  // Under the chat's detail key, so every event that refreshes the chat refreshes this too.
  context: (conversationId: string) => ['storefront-chats', 'detail', conversationId, 'context'] as const,
}

export function useStorefrontChats(view: ChatView, search: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: storefrontChatKeys.list(view, search),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/storefront-chats', {
        params: { query: { view, search: search || undefined, limit: 100 } },
      })
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
    placeholderData: (previous) => previous,
  })
}

export function useStorefrontChatSummary(enabled = true) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    enabled,
    queryKey: storefrontChatKeys.summary,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/storefront-chats/summary')
      if (error) throw error
      return data
    },
    refetchInterval: POLL_NORMAL,
  })
}

export function useStorefrontChat(conversationId: string | undefined) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: storefrontChatKeys.detail(conversationId ?? ''),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/storefront-chats/{conversation_id}', {
        params: { path: { conversation_id: conversationId ?? '' } },
      })
      if (error) throw error
      return data
    },
    enabled: !!conversationId,
    refetchInterval: POLL_NORMAL,
  })
}

/** The customer, products with our pricing and the cart, for a person taking the chat over. */
export function useStorefrontChatContext(conversationId: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: storefrontChatKeys.context(conversationId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/storefront-chats/{conversation_id}/context', {
        params: { path: { conversation_id: conversationId } },
      })
      if (error) throw error
      return data
    },
  })
}

/** Writes return the whole chat; put it in the cache and refresh the lists around it. */
function useSettleChat(conversationId: string) {
  const queryClient = useQueryClient()
  return {
    onSuccess: (data: ChatDetail) => queryClient.setQueryData(storefrontChatKeys.detail(conversationId), data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['storefront-chats', 'list'] })
      queryClient.invalidateQueries({ queryKey: storefrontChatKeys.summary })
    },
  }
}

function base64Blob(contentBase64: string, contentType: string) {
  const bytes = Uint8Array.from(atob(contentBase64), (character) => character.charCodeAt(0))
  return new Blob([bytes], { type: contentType })
}

/** A staff reply; with files it goes as one upload (photos, PDFs, spreadsheets, up to five). */
export function useReplyToChat(conversationId: string) {
  const getClient = useAuthenticatedClient()
  const { getAccessToken } = useAuth()
  const settle = useSettleChat(conversationId)
  return useMutation({
    mutationFn: async ({ body, files = [] }: { body: string; files?: ReplyAttachmentDraft[] }) => {
      if (files.length === 0) {
        const client = await getClient()
        const { data, error } = await client.POST('/dashboard/storefront-chats/{conversation_id}/messages', {
          params: { path: { conversation_id: conversationId } },
          body: { body },
        })
        if (error) throw error
        return data
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Not authenticated')
      const form = new FormData()
      form.append('body', body)
      files.forEach((file) => form.append('files', base64Blob(file.contentBase64, file.contentType), file.filename))
      const response = await fetch(
        `${API_BASE_URL}/dashboard/storefront-chats/${encodeURIComponent(conversationId)}/messages/files`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form },
      )
      const data: unknown = await response.json().catch(() => null)
      if (!response.ok) throw data ?? new Error(`Reply failed (${response.status})`)
      return data as ChatDetail
    },
    ...settle,
  })
}

/**
 * Take over (``human``) or hand back (``ai``). The chat switches on screen at once and switches
 * back if the server refuses.
 */
export function useSetChatMode(conversationId: string, me: { id: number; name: string } | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const settle = useSettleChat(conversationId)
  const key = storefrontChatKeys.detail(conversationId)
  return useMutation({
    mutationFn: async (mode: ChatMode) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/storefront-chats/{conversation_id}/mode', {
        params: { path: { conversation_id: conversationId } },
        body: { mode },
      })
      if (error) throw error
      return data
    },
    onMutate: async (mode) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ChatDetail>(key)
      if (previous) {
        queryClient.setQueryData<ChatDetail>(key, {
          ...previous,
          mode,
          needs_person: mode === 'human' ? false : previous.needs_person,
          // Handed back, the AI answers; taken over, the chat still waits until staff reply.
          awaiting_reply: mode === 'ai' ? false : previous.awaiting_reply,
          ...(mode === 'human' && me ? { assigned_staff_id: me.id, assigned_staff_name: me.name } : {}),
        })
      }
      return { previous }
    },
    onError: (_error, _mode, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    ...settle,
  })
}

/**
 * Edit (``body``) or delete (``null``) one of our replies (USA-1548). The chat changes on screen at
 * once and changes back if the server refuses: an edit shows the new text, a delete leaves the
 * note staff see where the reply was.
 */
export function useChangeChatReply(conversationId: string, me: { id: number; name: string } | undefined) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const settle = useSettleChat(conversationId)
  const key = storefrontChatKeys.detail(conversationId)
  return useMutation({
    mutationFn: async ({ messageId, body }: { messageId: string; body: string | null }) => {
      const client = await getClient()
      const path = { conversation_id: conversationId, message_id: messageId }
      const { data, error } =
        body === null
          ? await client.DELETE('/dashboard/storefront-chats/{conversation_id}/messages/{message_id}', {
              params: { path },
            })
          : await client.PATCH('/dashboard/storefront-chats/{conversation_id}/messages/{message_id}', {
              params: { path },
              body: { body },
            })
      if (error) throw error
      return data
    },
    onMutate: async ({ messageId, body }) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ChatDetail>(key)
      if (previous) {
        queryClient.setQueryData<ChatDetail>(key, {
          ...previous,
          messages: previous.messages.map((message) => {
            if (message.id !== messageId) return message
            if (body !== null) return { ...message, body, edited_at: new Date().toISOString() }
            const reply = message.author === 'ai' ? 'an AI reply' : 'a reply'
            return {
              ...message,
              author: 'system',
              body: `${me?.name ?? 'Staff'} deleted ${reply}`,
              product_cards: [],
              cart_actions: [],
              attachments: [],
            }
          }),
        })
      }
      return { previous }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    ...settle,
  })
}

/** Take a chat out of every list cached for one view, so it disappears at once. */
function removeFromLists(queryClient: QueryClient, view: ChatView, conversationId: string) {
  queryClient.setQueriesData<ChatList>({ queryKey: ['storefront-chats', 'list', view] }, (list) =>
    list
      ? {
          ...list,
          items: list.items.filter((item) => item.conversation_id !== conversationId),
          total: Math.max(0, list.total - 1),
        }
      : list,
  )
}

/**
 * Close (USA-1478) or reopen a chat: it moves between Open and Closed at once, and back
 * if the server refuses.
 */
function useCloseOrReopen(
  conversationId: string,
  action: 'close' | 'reopen',
  optimistic: (previous: ChatDetail) => ChatDetail,
) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const settle = useSettleChat(conversationId)
  const key = storefrontChatKeys.detail(conversationId)
  return useMutation({
    mutationFn: async () => {
      const client = await getClient()
      const path =
        action === 'close'
          ? '/dashboard/storefront-chats/{conversation_id}/close'
          : '/dashboard/storefront-chats/{conversation_id}/reopen'
      const { data, error } = await client.POST(path, { params: { path: { conversation_id: conversationId } } })
      if (error) throw error
      return data
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: storefrontChatKeys.all })
      const previous = queryClient.getQueryData<ChatDetail>(key)
      const lists = queryClient.getQueriesData<ChatList>({ queryKey: ['storefront-chats', 'list'] })
      if (previous) queryClient.setQueryData<ChatDetail>(key, optimistic(previous))
      if (action === 'close') {
        removeFromLists(queryClient, 'open', conversationId)
      } else {
        removeFromLists(queryClient, 'closed', conversationId)
      }
      return { previous, lists }
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
      context?.lists.forEach(([listKey, list]) => queryClient.setQueryData(listKey, list))
    },
    ...settle,
  })
}

export function useCloseChat(conversationId: string, me: { id: number; name: string } | undefined) {
  return useCloseOrReopen(conversationId, 'close', (previous) => ({
    ...previous,
    needs_person: false,
    awaiting_reply: false,
    unread_count: 0,
    closed_at: new Date().toISOString(),
    closed_by_name: me?.name ?? null,
  }))
}

export function useReopenChat(conversationId: string) {
  return useCloseOrReopen(conversationId, 'reopen', (previous) => ({
    ...previous,
    closed_at: null,
    closed_by_name: null,
  }))
}

export function useAssignChat(conversationId: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  const settle = useSettleChat(conversationId)
  const key = storefrontChatKeys.detail(conversationId)
  return useMutation({
    mutationFn: async (staff: { id: number; name: string } | null) => {
      const client = await getClient()
      const { data, error } = await client.PUT('/dashboard/storefront-chats/{conversation_id}/assignment', {
        params: { path: { conversation_id: conversationId } },
        body: { staff_id: staff?.id ?? null },
      })
      if (error) throw error
      return data
    },
    onMutate: async (staff) => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ChatDetail>(key)
      if (previous) {
        queryClient.setQueryData<ChatDetail>(key, {
          ...previous,
          assigned_staff_id: staff?.id ?? null,
          assigned_staff_name: staff?.name ?? null,
        })
      }
      return { previous }
    },
    onError: (_error, _staff, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous)
    },
    ...settle,
  })
}

/** Follow every chat (list room) while mounted: refresh lists, summaries and open chats on events. */
export function useStorefrontChatEvents(onEvent?: (event: string, conversationId: string) => void) {
  const { on, emit, isConnected } = useSocketContext()
  const queryClient = useQueryClient()
  const callback = useRef(onEvent)
  useEffect(() => {
    callback.current = onEvent
  }, [onEvent])

  useEffect(() => {
    // Rooms do not survive a reconnect, so join again every time the socket connects.
    if (isConnected) emit('storefront_chat:join', {})
  }, [emit, isConnected])

  useEffect(() => {
    const refresh = (event: string) => (data: { conversation_id?: string }) => {
      queryClient.invalidateQueries({ queryKey: ['storefront-chats', 'list'] })
      queryClient.invalidateQueries({ queryKey: storefrontChatKeys.summary })
      if (data?.conversation_id) {
        queryClient.invalidateQueries({ queryKey: storefrontChatKeys.detail(data.conversation_id) })
        callback.current?.(event, data.conversation_id)
      }
    }
    const events = ['storefront_chat.message', 'storefront_chat.mode', 'storefront_chat.page', 'storefront_chat.updated']
    const unsubs = events.map((event) => on(event, refresh(event)))
    return () => unsubs.forEach((unsub) => unsub())
  }, [on, queryClient])
}

/**
 * The AI's answer as it streams in for the open chat, until the finished message arrives.
 * Returns the message id and text so far, or null.
 */
export function useStreamingAnswer(conversationId: string | undefined, messages: ChatMessage[] | undefined) {
  const { on, emit, isConnected } = useSocketContext()
  const [stream, setStream] = useState<{ conversationId: string; messageId: string; text: string } | null>(null)

  useEffect(() => {
    if (!conversationId || !isConnected) return
    emit('storefront_chat:join', { conversation_id: conversationId })
    return () => emit('storefront_chat:leave', { conversation_id: conversationId })
  }, [conversationId, emit, isConnected])

  useEffect(() => {
    if (!conversationId) return
    const unsubs = [
      on('storefront_chat.delta', (data: { conversation_id: string; message_id: string; text: string }) => {
        if (data.conversation_id !== conversationId) return
        setStream((current) =>
          current && current.messageId === data.message_id && current.conversationId === conversationId
            ? { ...current, text: current.text + data.text }
            : { conversationId, messageId: data.message_id, text: data.text },
        )
      }),
      // A takeover stops the answer; whatever streamed is discarded.
      on('storefront_chat.mode', (data: { conversation_id: string; mode: ChatMode }) => {
        if (data.conversation_id === conversationId && data.mode === 'human') setStream(null)
      }),
    ]
    return () => unsubs.forEach((unsub) => unsub())
  }, [conversationId, on])

  if (!stream || stream.conversationId !== conversationId) return null
  if (messages?.some((m) => m.id === stream.messageId)) return null
  return stream
}
